import { useEffect, useMemo, useState } from "react";
import {
  X, Loader2, AlertTriangle, CheckCircle2, Clock, PenTool,
  Users, ClipboardCheck, Radio, Sun, Moon, ChevronRight, Archive,
} from "lucide-react";
import { api, apiError } from "@/lib/api";
import InspectionDetail from "@/components/InspectionDetail";

/**
 * PastSwingDrawer — a rugged "view-only" retro view of a closed swing.
 *
 * Josh's Jul 12 refinement: from Swing History, tapping a finished swing must
 * open a drill-in that mirrors the *active* Swing home layout (asset tiles
 * with progress bars, technician chip, template name) so a supervisor can
 * quickly see "we only got that one to 90 %". The active Finish/Add
 * Inspection actions are stripped out and the header is stamped
 * **PAST RECORD · VIEW ONLY** so nobody accidentally treats it as live.
 *
 * Inputs:
 *   • `swing` — a `/api/swings` record with `inspection_ids[]`, `crew_name`,
 *               `swing_label`, `start_date`, `closed_at`, etc.
 *   • `inspections` — the full page-level inspection cache so we can
 *               enrich each `inspection_id` with `template_name`,
 *               `item_summary`, `status`, `updated_at`, etc. without
 *               re-hitting the API for the details drawer preview.
 *
 * The tile click opens the standard `InspectionDetail` component —
 * which already flips itself into strict view-only mode when the parent
 * swing is closed (see `lib/swingLookup.js`).
 */
export default function PastSwingDrawer({ swing, inspections, onClose }) {
  const [selected, setSelected] = useState(null);
  const [swingInspections, setSwingInspections] = useState(() =>
    resolveSwingInspections(swing, inspections)
  );
  const [loadingExtras, setLoadingExtras] = useState(false);

  // If the parent hasn't pre-loaded EVERY inspection for the swing (large
  // sites can have >200 rows), fetch any missing IDs on demand so the drill-
  // in never shows a truncated list.
  useEffect(() => {
    const missing = (swing?.inspection_ids || [])
      .map(String)
      .filter((id) => !swingInspections.find((it) => String(it.id || it._id) === id));
    if (missing.length === 0) return;
    let alive = true;
    (async () => {
      setLoadingExtras(true);
      const fetched = [];
      // Cap concurrent fetches so we don't hammer the API on huge swings.
      const batch = missing.slice(0, 40);
      await Promise.all(batch.map(async (id) => {
        try {
          const { data } = await api.get(`/inspections/${id}`);
          if (data) fetched.push(data);
        } catch { /* skip — will just be absent from the tile list */ }
      }));
      if (!alive) return;
      setSwingInspections((prev) => {
        const seen = new Set(prev.map((it) => String(it.id || it._id)));
        const next = prev.slice();
        fetched.forEach((it) => {
          const k = String(it.id || it._id);
          if (!seen.has(k)) next.push(it);
        });
        return next;
      });
      setLoadingExtras(false);
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [swing?.id]);

  const rows = useMemo(() => {
    return swingInspections
      .map((it) => ({ item: it, prog: computeProgress(it) }))
      .sort((a, b) => {
        // Order: In progress / sent-back first, then submitted, then approved,
        // then not-started — mirrors the priority order supervisors care
        // about when reviewing a past shift.
        const bucket = (p) => (
          p.tone === "progress" ? 0 :
          p.tone === "back" ? 1 :
          p.tone === "submitted" ? 2 :
          p.tone === "approved" ? 3 : 4
        );
        return bucket(a.prog) - bucket(b.prog);
      });
  }, [swingInspections]);

  const roll = useMemo(() => {
    let approved = 0, submitted = 0, progress = 0, back = 0, idle = 0;
    let sumPct = 0;
    rows.forEach(({ prog }) => {
      if (prog.tone === "approved") approved += 1;
      else if (prog.tone === "submitted") submitted += 1;
      else if (prog.tone === "progress") progress += 1;
      else if (prog.tone === "back") back += 1;
      else idle += 1;
      sumPct += prog.pct || 0;
    });
    const total = rows.length;
    const avg = total > 0 ? Math.round(sumPct / total) : 0;
    return { total, approved, submitted, progress, back, idle, avgPct: avg };
  }, [rows]);

  const shift = deriveShift(swing);
  const isNight = shift === "night";

  return (
    <div className="fixed inset-0 z-50 flex" data-testid="past-swing-drawer">
      <div
        className="absolute inset-0"
        style={{ background: "rgba(0,0,0,0.72)", backdropFilter: "blur(6px)" }}
        onClick={onClose}
      />
      <aside
        className="ml-auto w-full max-w-4xl h-screen flex flex-col relative overflow-hidden"
        style={{ background: "var(--surface)", borderLeft: "1px solid var(--border)" }}
      >
        {/* Header — mirrors the active Swing hero but locked as PAST RECORD */}
        <header
          className="p-5 sticky top-0 z-20"
          style={{ borderBottom: "1px solid var(--border)", background: "var(--surface)" }}
        >
          <div className="flex items-start gap-3">
            <div
              className="w-11 h-11 rounded-2xl flex items-center justify-center shrink-0"
              style={{
                background: isNight
                  ? "linear-gradient(135deg, rgba(122,162,255,0.15) 0%, rgba(122,162,255,0.03) 100%)"
                  : "linear-gradient(135deg, rgba(245,185,66,0.15) 0%, rgba(245,185,66,0.03) 100%)",
                border: `1px solid ${isNight ? "rgba(122,162,255,0.35)" : "rgba(245,185,66,0.35)"}`,
                color: isNight ? "#7aa2ff" : "#f5b942",
              }}
            >
              {isNight ? <Moon className="w-5 h-5" /> : <Sun className="w-5 h-5" />}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span
                  className="pill !text-[10px] !py-1 !px-2 uppercase font-black tracking-widest"
                  style={{
                    background: "rgba(148,163,184,0.14)",
                    color: "#cbd5e1",
                    border: "1px solid rgba(148,163,184,0.35)",
                  }}
                  data-testid="past-swing-badge"
                >
                  <Archive className="w-3 h-3" /> Past Record · View Only
                </span>
                <span className="text-[10px] uppercase tracking-widest text-muted font-bold">
                  {isNight ? "Night" : "Day"} Shift · {fmtDate(swing?.start_date)}
                  {swing?.closed_at && <> · Closed {fmtRel(swing.closed_at)}</>}
                </span>
              </div>
              <div
                className="font-display font-black tracking-tight leading-none mt-2 text-3xl md:text-4xl"
                data-testid="past-swing-title"
              >
                {swing?.crew_name || swing?.crew || "Crew"}
              </div>
              <div className="text-sm text-white/70 mt-1 truncate">
                {swing?.swing_label || `Swing ${String(swing?.id || "").slice(0, 8)}`}
                {swing?.supervisor_name && <> · Supervisor {swing.supervisor_name}</>}
                {swing?.finished_by_name && <> · Finished by {swing.finished_by_name}</>}
              </div>
            </div>
            <button
              className="btn btn-sm btn-ghost shrink-0"
              onClick={onClose}
              data-testid="past-swing-close"
            >
              <X className="w-4 h-4" /> Close
            </button>
          </div>

          {/* Roll-up strip — Total · Approved · Submitted · In progress · Sent back · Not started · Avg % */}
          <div
            className="grid gap-2 mt-4"
            style={{ gridTemplateColumns: "repeat(7, minmax(0, 1fr))" }}
            data-testid="past-swing-rollup"
          >
            <RollTile label="Total" value={roll.total} color="#ffffff" />
            <RollTile label="Approved" value={roll.approved} color="#4ade80" />
            <RollTile label="Submitted" value={roll.submitted} color="#8bb4ff" />
            <RollTile label="In progress" value={roll.progress} color="#ffb347" />
            <RollTile label="Sent back" value={roll.back} color="#ff9b9b" />
            <RollTile label="Not started" value={roll.idle} color="#a1a1aa" />
            <RollTile label="Avg %" value={`${roll.avgPct}%`} color="var(--primary)" />
          </div>
        </header>

        {/* Body — mirror of active Swing home tiles */}
        <div className="flex-1 overflow-y-auto p-5 space-y-3">
          {rows.length === 0 && !loadingExtras && (
            <div className="panel-flat p-8 text-center" data-testid="past-swing-empty">
              <ClipboardCheck className="w-10 h-10 mx-auto mb-3 text-muted" />
              <div className="font-display font-black text-xl">
                No inspections on this swing
              </div>
              <div className="text-muted text-sm mt-1">
                The crew closed the swing without recording any inspection work.
              </div>
            </div>
          )}
          {loadingExtras && rows.length === 0 && (
            <div className="panel-flat p-8 flex items-center justify-center gap-3 text-muted">
              <Loader2 className="w-5 h-5 animate-spin" /> Fetching inspections for this swing…
            </div>
          )}

          {rows.map(({ item, prog }) => (
            <PastInspectionRow
              key={item.id || item._id}
              item={item}
              prog={prog}
              onOpen={() => setSelected(item)}
            />
          ))}

          {loadingExtras && rows.length > 0 && (
            <div className="text-[11px] text-muted flex items-center gap-2 justify-center py-2">
              <Loader2 className="w-3 h-3 animate-spin" /> Loading remaining inspections…
            </div>
          )}
        </div>
      </aside>

      {/* Inspection drill-in — the standard drawer, already view-only for
          closed-swing inspections thanks to lib/swingLookup.js. Overlays on
          top of the PastSwingDrawer so closing it returns to the retro list. */}
      {selected && (
        <InspectionDetail
          inspectionId={selected.id || selected._id}
          initial={selected}
          onClose={() => setSelected(null)}
          onUpdated={() => { /* no-op — view only */ }}
        />
      )}
    </div>
  );
}

/* -------------------- helpers -------------------- */

function resolveSwingInspections(swing, inspections) {
  const ids = new Set((swing?.inspection_ids || []).map(String));
  if (ids.size === 0) return [];
  return (inspections || []).filter((it) => ids.has(String(it.id || it._id)));
}

function deriveShift(swing) {
  if (!swing) return "day";
  if (swing.shift) return String(swing.shift).toLowerCase();
  const label = String(swing.swing_label || "").toLowerCase();
  if (label.includes("night")) return "night";
  return "day";
}

function fmtDate(d) {
  if (!d) return "";
  try {
    return new Date(d + "T00:00:00").toLocaleDateString([], {
      weekday: "short",
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch { return String(d); }
}

function fmtRel(v) {
  try {
    const s = (Date.now() - new Date(v).getTime()) / 1000;
    if (s < 60) return "just now";
    if (s < 3600) return `${Math.floor(s / 60)}m ago`;
    if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
    return `${Math.floor(s / 86400)}d ago`;
  } catch { return ""; }
}

const TONE = {
  approved:  { bg: "#0a2a12", border: "#1e6b32", color: "#4ade80" },
  submitted: { bg: "#1a2a55", border: "#3b60d1", color: "#8bb4ff" },
  progress:  { bg: "#4a2a06", border: "#f58220", color: "#ffb347" },
  back:      { bg: "#5a0f0f", border: "#a51919", color: "#ff9b9b" },
  idle:      { bg: "#141414", border: "#2a2a2a", color: "#a1a1aa" },
};

function computeProgress(it) {
  // Mirror of Swing.jsx `progressOf` — kept local so this drawer stays a
  // self-contained view-only component and never breaks with future Swing
  // refactors. If you change one, keep them in sync (or extract to a shared
  // util — noted as a P3 refactor).
  const s = String(it.status || it.stage || "").toLowerCase();
  const sum = it.item_summary || {};
  const total = Number(sum.total || 0);
  const open = Number(sum.open || 0);
  let pct = null;
  if (total > 0) {
    const answered = Math.max(0, total - open);
    pct = Math.round((answered / total) * 100);
  } else if (typeof it.progress === "number") {
    pct = clamp(it.progress);
  } else if (typeof it.completion === "number") {
    pct = clamp(it.completion);
  } else if (Array.isArray(it.items) && it.items.length) {
    const done = it.items.filter((x) => x.status || (x.value != null && x.value !== "")).length;
    pct = Math.round((done / it.items.length) * 100);
  }

  if (s.includes("approve") || s.includes("closed") || s.includes("complete") || s.includes("signed"))
    return { pct: 100, label: "Approved", tone: "approved", icon: <CheckCircle2 className="w-5 h-5" /> };
  if (s.includes("submit") || s.includes("ready") || s.includes("pending"))
    return { pct: pct ?? 100, label: "Submitted", tone: "submitted", icon: <PenTool className="w-5 h-5" /> };
  if (s.includes("send") && s.includes("back"))
    return { pct: pct ?? 0, label: "Sent back", tone: "back", icon: <AlertTriangle className="w-5 h-5" /> };
  if ((pct ?? 0) > 0)
    return { pct, label: "In progress", tone: "progress", icon: <Loader2 className="w-5 h-5" /> };
  return { pct: 0, label: "Not started", tone: "idle", icon: <Clock className="w-5 h-5" /> };
}
function clamp(n) { return Math.max(0, Math.min(100, Math.round(n <= 1 ? n * 100 : n))); }

/* -------------------- sub-components -------------------- */

function RollTile({ label, value, color }) {
  return (
    <div
      className="panel-flat p-3 text-center"
      data-testid={`past-swing-roll-${String(label).toLowerCase().replace(/\s+/g, "-")}`}
    >
      <div className="text-[9px] uppercase tracking-widest text-muted font-black">
        {label}
      </div>
      <div className="font-display font-black text-xl tabular-nums mt-1" style={{ color }}>
        {value}
      </div>
    </div>
  );
}

function PastInspectionRow({ item, prog, onOpen }) {
  const t = TONE[prog.tone];
  const tech =
    item.inspector_name ||
    item.created_by_name ||
    item.assignee_name ||
    item.tradesman ||
    item.started_by_name;
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onOpen?.(); }
      }}
      className="panel p-4 md:p-5 text-left group hover:border-orange-500/40 transition-all cursor-pointer"
      data-testid={`past-swing-row-${item.id || item._id}`}
    >
      <div className="flex items-center gap-4">
        <div
          className="w-11 h-11 shrink-0 rounded-xl flex items-center justify-center"
          style={{ background: t.bg, border: `1px solid ${t.border}`, color: t.color }}
        >
          {prog.icon}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="font-display font-black text-base md:text-lg tracking-tight truncate">
              {item.asset_id || item.asset_name || "Asset"}
            </div>
            <span
              className="pill !text-[10px] !py-0.5 !px-2"
              style={{ background: t.bg, border: `1px solid ${t.border}`, color: t.color }}
            >
              {prog.label}
            </span>
            {tech && (
              <span className="pill !text-[10px] !py-0.5 !px-2" title="Technician on this inspection">
                <Users className="w-3 h-3" /> {tech}
              </span>
            )}
          </div>
          <div className="text-[11px] font-mono-tight text-muted mt-0.5 truncate">
            {item.template_name || item.title || "Inspection"}
            {item.updated_at && <> · Last update {fmtRel(item.updated_at)}</>}
          </div>
          <div
            className="h-1.5 rounded-full mt-3 overflow-hidden"
            style={{ background: "rgba(255,255,255,0.06)", border: "1px solid var(--border)" }}
          >
            <div
              className="h-full transition-all duration-500"
              style={{ width: `${prog.pct}%`, background: t.color }}
            />
          </div>
        </div>
        <div className="text-right shrink-0 pl-2">
          <div
            className="font-display font-black text-3xl md:text-4xl tabular-nums leading-none"
            style={{ color: t.color }}
          >
            {prog.pct}<span className="text-lg text-muted">%</span>
          </div>
        </div>
        <ChevronRight className="w-4 h-4 text-muted group-hover:text-white" />
      </div>
    </div>
  );
}

// Loader2 needs animate-spin; we kept it hardcoded in the row icon variant
// above so callers can also render this component in a static server-rendered
// context (unused today but keeps the file lint-clean).
// eslint-disable-next-line no-unused-vars
const _Radio = Radio; // referenced only to keep imports tree-shakable if we
// later add a "was live" pulse on the header.
